import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield, CheckCircle2, Loader2 } from "lucide-react";

export default function VerifyDialog({ open, onOpenChange }) {
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => { setCode(""); setVerified(false); }, 200);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Please enter a verification code");
    setVerifying(true);
    // Simulate a quick lookup
    await new Promise(r => setTimeout(r, 800));
    setVerifying(false);
    setVerified(true);
    toast.success("Code received — verified.");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="verify-dialog"
        className="bg-[#0A0F1A] border border-white/10 text-white max-w-md p-0 overflow-hidden"
      >
        <div className="bg-gradient-to-b from-[#39FF14]/5 to-transparent border-b border-white/5 px-6 py-5">
          <DialogHeader>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#39FF14] mb-2 font-semibold">
              Anti-Counterfeit
            </p>
            <DialogTitle className="font-headline text-2xl font-bold text-white">Verify Your Product</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Every Supreme Laboratories product comes with a unique verification code printed on the box.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="px-6 py-5">
          {!verified ? (
            <form onSubmit={verify} className="space-y-3">
              <Input
                data-testid="verify-dialog-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ENTER VERIFICATION CODE"
                className="bg-[#020409] border-white/10 focus:border-[#39FF14] focus-visible:ring-[#39FF14]/30 text-white uppercase tracking-widest text-xs h-11"
              />
              <Button
                type="submit"
                data-testid="verify-dialog-submit"
                disabled={verifying}
                className="w-full h-11 bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-[0.15em] text-xs shadow-[0_0_15px_rgba(57,255,20,0.4)]"
              >
                {verifying ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Verifying</> : <><Shield className="w-3.5 h-3.5 mr-1.5" /> Verify Now</>}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4" data-testid="verify-dialog-success">
              <div className="w-14 h-14 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center mx-auto mb-3 shadow-[0_0_25px_rgba(57,255,20,0.3)]">
                <CheckCircle2 className="w-7 h-7 text-[#39FF14]" />
              </div>
              <h3 className="font-headline text-lg font-bold mb-1">Code Received</h3>
              <p className="text-zinc-400 text-sm">Our team will validate and contact you shortly.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
